import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { getClientVaultContext } from "./api";
import type { ClientVaultContext, ClientVaultContextVault } from "./types";

const SELECTED_VAULT_KEY = "ktd_selected_client_vault_v2";
const UNASSIGNED_CLIENT_MESSAGE =
  "No Memory Media Vault client account or event vault is assigned to this login yet. Please ask Kreatethedate to confirm portal access.";

interface ClientVaultContextState {
  context: ClientVaultContext | null;
  selectedVault: ClientVaultContextVault | null;
  selectedVaultId: string;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
  selectVault: (vaultId: string) => void;
}

const SharedClientVaultContext = createContext<ClientVaultContextState | null>(null);

function readStoredVaultId() {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(SELECTED_VAULT_KEY) || "";
}

function storeVaultId(vaultId: string) {
  if (typeof window === "undefined") return;
  if (vaultId) {
    window.localStorage.setItem(SELECTED_VAULT_KEY, vaultId);
  } else {
    window.localStorage.removeItem(SELECTED_VAULT_KEY);
  }
}

function useClientVaultContextState(): ClientVaultContextState {
  const [context, setContext] = useState<ClientVaultContext | null>(null);
  const [selectedVaultId, setSelectedVaultId] = useState(readStoredVaultId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setContext(await getClientVaultContext());
    } catch (loadError) {
      const message = loadError instanceof Error ? loadError.message : "";
      setError(message.includes("Client context is required") || message.includes("Client not found")
        ? UNASSIGNED_CLIENT_MESSAGE
        : message || "Unable to load vault context.");
      setContext(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const selectedVault = useMemo<ClientVaultContextVault | null>(() => {
    if (!context?.vaults.length) return null;
    return context.vaults.find((vault) => vault.id === selectedVaultId) || context.vaults[0];
  }, [context, selectedVaultId]);

  useEffect(() => {
    if (!context?.vaults.length) {
      storeVaultId("");
      return;
    }
    if (!selectedVaultId || !context.vaults.some((vault) => vault.id === selectedVaultId)) {
      const firstVaultId = context.vaults[0].id;
      setSelectedVaultId(firstVaultId);
      storeVaultId(firstVaultId);
    }
  }, [context, selectedVaultId]);

  const selectVault = useCallback((vaultId: string) => {
    setSelectedVaultId(vaultId);
    storeVaultId(vaultId);
  }, []);

  return {
    context,
    selectedVault,
    selectedVaultId: selectedVault?.id || selectedVaultId,
    loading,
    error,
    refresh,
    selectVault,
  };
}

export function ClientVaultContextProvider({ children }: { children: ReactNode }) {
  const value = useClientVaultContextState();
  return createElement(SharedClientVaultContext.Provider, { value }, children);
}

export function useClientVaultContext() {
  const context = useContext(SharedClientVaultContext);
  if (!context) throw new Error("ClientVaultContextProvider is required.");
  return context;
}

export function vaultQueryParams(context: ClientVaultContext | null, vault: ClientVaultContextVault | null) {
  if (!context || !vault) return {};
  return {
    clientId: context.client.id,
    eventId: vault.event.id,
    vaultId: vault.id,
    vaultEventId: vault.event.vaultEventId,
  };
}
